// DOM controller: menus, modals, HUD, banners, toasts, profile card and leaderboard table.
import { SHOTS_PER_ROUND, GOALS_TO_ADVANCE } from '../game/config.js';

const $ = (id) => document.getElementById(id);
const fmt = (n) => Math.round(n).toLocaleString('en-US');

// Neutral avatar used when start.ng has not provided a face picture (offline / local play).
const FALLBACK_FACE = 'data:image/svg+xml,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#d8fd71"/>'
  + '<circle cx="32" cy="25" r="12" fill="#292a23"/><path d="M10 60c3-14 12-20 22-20s19 6 22 20z" fill="#292a23"/></svg>');

export class UI {
  constructor() {
    this.el = {
      loading: $('loading'), loadingText: $('loading-text'), fatal: $('fatal'), fatalText: $('fatal-text'),
      menu: $('menu'), menuBest: $('menu-best'), menuControls: $('menu-controls'),
      hud: $('hud'), round: $('hud-round'), keeper: $('hud-keeper'), dots: $('hud-dots'), points: $('hud-points'),
      target: $('hud-target'), hint: $('hud-hint'), power: $('power'), powerFill: $('power-fill'), curve: $('curve'),
      shoot: $('btn-shoot'), banner: $('banner'), bannerTitle: $('banner-title'), bannerSub: $('banner-sub'), bannerTags: $('banner-tags'),
      replay: $('replay-badge'), toast: $('toast'), pause: $('btn-pause'), mute: $('btn-mute'),
      card: $('player-card'), face: $('player-face'), name: $('player-name'), account: $('player-account'), signIn: $('player-sign-in'),
      boardBody: $('board-body'),
      overEyebrow: $('over-eyebrow'), overTitle: $('over-title'), overPoints: $('over-points'), overGoals: $('over-goals'),
      overAcc: $('over-acc'), overRounds: $('over-rounds'), overBest: $('over-best'), overSave: $('over-save'),
    };
    this.modals = ['modal-howto', 'modal-settings', 'modal-board', 'modal-pause', 'modal-over'].map($);
    this.stack = [];
    this.toastTimer = 0;
    this.bannerTimer = 0;
    for (const m of this.modals) {
      m.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]')) this.closeModal(m.id);
        else if (e.target === m && m.id !== 'modal-over' && m.id !== 'modal-pause') this.closeModal(m.id);
      });
    }
    $('btn-reload').addEventListener('click', () => location.reload());
    this.el.face.src = FALLBACK_FACE;
    this.el.face.onerror = () => { this.el.face.src = FALLBACK_FACE; };
  }

  // ---------- screens ----------
  setLoading(text) { this.el.loadingText.textContent = text; }
  hideLoading() { this.el.loading.classList.add('done'); setTimeout(() => { this.el.loading.hidden = true; }, 600); }
  fatal(msg) { this.el.fatalText.textContent = msg; this.el.fatal.hidden = false; this.el.loading.hidden = true; }

  showMenu(show) { this.el.menu.hidden = !show; }
  showHud(show) { this.el.hud.hidden = !show; this.el.pause.hidden = !show; document.body.classList.toggle('playing', show); }

  setBest(best) {
    const b = this.el.menuBest;
    b.replaceChildren();
    if (!best) { b.textContent = 'No personal best yet: take your first penalty'; return; }
    b.append('Personal best: ');
    const s = document.createElement('b');
    s.textContent = `${fmt(best.points)} pts`;
    b.append(s, ` · ${best.goals} goals · ${best.rounds} round${best.rounds === 1 ? '' : 's'} cleared`);
  }

  setControlsHint(touch) {
    this.el.menuControls.textContent = touch
      ? 'Drag to aim · hold the button to power up · release to shoot'
      : 'Mouse to aim · hold click or Space to power up · release to shoot · Q/E curve';
  }

  // ---------- modals ----------
  openModal(id) {
    const m = $(id);
    if (!m.hidden) return;
    m.hidden = false;
    this.stack.push(id);
    m.style.zIndex = String(50 + this.stack.length); // the newest modal is always on top
    const focusable = m.querySelector('.btn.primary, button, input, select');
    focusable?.focus({ preventScroll: true });
  }
  closeModal(id) {
    const m = $(id);
    if (m.hidden) return;
    m.hidden = true;
    this.stack = this.stack.filter((x) => x !== id);
    this.onModalClosed?.(id);
  }
  closeTopModal() { const id = this.stack[this.stack.length - 1]; if (id) this.closeModal(id); return id; }
  isOpen(id) { return !$(id).hidden; }
  anyModal() { return this.stack.length > 0; }

  toast(text, ms = 3200) {
    const t = this.el.toast;
    t.textContent = text;
    t.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => { t.hidden = true; }, ms);
  }

  // ---------- HUD ----------
  setRound(round, keeperName) {
    this.el.round.textContent = `Round ${round + 1}`;
    this.el.keeper.textContent = `vs ${keeperName}`;
    this.el.target.textContent = `Score ${GOALS_TO_ADVANCE} of ${SHOTS_PER_ROUND} to advance`;
  }

  setPoints(points, bump = false) {
    const p = this.el.points;
    p.textContent = fmt(points);
    if (bump) { p.classList.remove('bump'); void p.offsetWidth; p.classList.add('bump'); }
  }

  setDots(results) {
    const d = this.el.dots;
    d.replaceChildren();
    for (let i = 0; i < SHOTS_PER_ROUND; i++) {
      const dot = document.createElement('i');
      const r = results[i];
      if (r === 'goal') { dot.className = 'goal'; dot.textContent = '✓'; } else if (r) { dot.className = 'fail'; dot.textContent = '✕'; } else if (i === results.length) dot.className = 'cur';
      d.append(dot);
    }
  }

  setHint(text) { this.el.hint.textContent = text; this.el.hint.hidden = !text; }

  setPower(p, zone) {
    const f = this.el.powerFill;
    f.style.height = p <= 0.001 ? '0' : `calc(${(p * 100).toFixed(1)}% - 6px)`;
    f.classList.toggle('perfect', zone === 'perfect');
    f.classList.toggle('over', zone === 'over');
  }

  setCurve(value) {
    for (const b of this.el.curve.querySelectorAll('button')) {
      const on = Number(b.dataset.curve) === value;
      b.classList.toggle('on', on);
      b.setAttribute('aria-checked', String(on));
      b.setAttribute('role', 'radio');
    }
  }

  setShootEnabled(on) { this.el.shoot.disabled = !on; }
  setCharging(on) { this.el.shoot.classList.toggle('charging', on); }

  banner(title, { sub = '', tags = [], kind = 'info', ms = 1800 } = {}) {
    const b = this.el.banner;
    b.className = `banner ${kind}`;
    // restart CSS animations
    this.el.bannerTitle.replaceWith(this.el.bannerTitle = this.el.bannerTitle.cloneNode(false));
    this.el.bannerTitle.textContent = title;
    this.el.bannerSub.textContent = sub;
    this.el.bannerTags.replaceChildren(...tags.map((t, i) => {
      const li = document.createElement('li');
      li.style.animationDelay = `${0.15 + i * 0.07}s`;
      li.append(t.label);
      if (t.points) { const pts = document.createElement('b'); pts.textContent = `+${t.points}`; li.append(pts); }
      return li;
    }));
    b.hidden = false;
    clearTimeout(this.bannerTimer);
    if (ms > 0) this.bannerTimer = setTimeout(() => { b.hidden = true; }, ms);
  }
  hideBanner() { clearTimeout(this.bannerTimer); this.el.banner.hidden = true; }

  showReplay(on) { this.el.replay.hidden = !on; document.body.classList.toggle('replaying', on); }

  setMuted(m) {
    this.el.mute.classList.toggle('muted', m);
    this.el.mute.setAttribute('aria-label', m ? 'Unmute sound' : 'Mute sound');
  }

  // ---------- start.ng profile card ----------
  setProfile(profile, status) {
    this.el.face.src = profile.face || FALLBACK_FACE;
    this.el.name.textContent = profile.name;
    this.el.account.textContent = profile.account;
    this.el.signIn.hidden = profile.signedIn || status === 'loading';
  }

  // ---------- game over ----------
  showGameOver(run, stats, best, isNewBest) {
    const { el } = this;
    el.overEyebrow.textContent = 'Full time';
    el.overTitle.textContent = run.roundsCleared > 0
      ? `Knocked out in round ${run.round + 1}`
      : 'Knocked out in round 1';
    el.overPoints.textContent = fmt(stats.points);
    el.overGoals.textContent = `${run.goals}/${run.shots}`;
    el.overAcc.textContent = `${stats.accuracy}%`;
    el.overRounds.textContent = String(stats.rounds);
    el.overBest.textContent = isNewBest ? '★ New personal best!' : best ? `Personal best: ${fmt(best.points)} pts` : '';
    this.openModal('modal-over');
  }

  /** state: { kind: 'saving'|'ok'|'err'|'guest'|'offline', text, action?: { label, onClick } } */
  setSaveStatus(state) {
    const s = this.el.overSave;
    s.className = `save-status ${state.kind === 'ok' ? 'ok' : state.kind === 'err' ? 'err' : ''}`;
    s.replaceChildren();
    if (state.kind === 'saving') { const sp = document.createElement('div'); sp.className = 'spinner'; s.append(sp); }
    const span = document.createElement('span');
    span.textContent = state.text;
    s.append(span);
    if (state.action) {
      const b = document.createElement('button');
      b.className = 'chip-btn';
      b.textContent = state.action.label;
      b.addEventListener('click', state.action.onClick, { once: true });
      s.append(b);
    }
  }

  // ---------- leaderboard ----------
  boardLoading() {
    const box = this.el.boardBody;
    box.replaceChildren();
    const msg = document.createElement('div');
    msg.className = 'board-msg';
    const sp = document.createElement('div');
    sp.className = 'spinner';
    msg.append(sp, 'Loading the start.ng leaderboard…');
    box.append(msg);
  }

  boardMessage(text, action) {
    const box = this.el.boardBody;
    const msg = document.createElement('div');
    msg.className = 'board-msg';
    msg.textContent = text;
    if (action) {
      const b = document.createElement('button');
      b.className = 'btn primary';
      b.style.marginTop = '14px';
      b.textContent = action.label;
      b.addEventListener('click', action.onClick);
      msg.append(document.createElement('br'), b);
    }
    box.replaceChildren(msg);
  }

  /** entries: start.ng leaderboard rows; meId: the signed-in player's id (or null). */
  boardTable(entries, meId, footer) {
    const box = this.el.boardBody;
    const wrap = document.createElement('div');
    wrap.className = 'board-scroll';
    const table = document.createElement('table');
    table.className = 'board-table';
    const head = document.createElement('tr');
    for (const h of ['#', 'Player', 'Points', 'Goals', 'Rounds', 'Acc.']) { const th = document.createElement('th'); th.textContent = h; head.append(th); }
    const thead = document.createElement('thead');
    thead.append(head);
    const tbody = document.createElement('tbody');
    for (const e of entries) {
      const tr = document.createElement('tr');
      const me = meId != null && e.player_id === meId;
      if (me) tr.className = 'me';
      const stats = e.stats || {};
      const cells = [
        String(e.rank ?? ''),
        null,
        fmt(stats.points ?? e.score ?? 0),
        String(stats.goals ?? '–'),
        String(stats.rounds ?? '–'),
        stats.accuracy != null ? `${stats.accuracy}%` : '–',
      ];
      cells.forEach((c, i) => {
        const td = document.createElement('td');
        if (i === 1) {
          const dot = document.createElement('span');
          dot.className = 'dot';
          dot.style.background = /^#[0-9a-f]{3,8}$/i.test(e.color || '') ? e.color : '#888';
          td.append(dot, String(e.name ?? 'Player'));
          if (me) { const y = document.createElement('span'); y.className = 'you'; y.textContent = 'YOU'; td.append(y); }
        } else td.textContent = c;
        tr.append(td);
      });
      tbody.append(tr);
    }
    table.append(thead, tbody);
    wrap.append(table);
    box.replaceChildren(wrap);
    if (footer) {
      const p = document.createElement('p');
      p.className = 'muted';
      p.style.textAlign = 'center';
      p.textContent = footer;
      box.append(p);
    }
  }
}
